import { Button, Heading } from "@stacklet/ui";
import { graphql, useFragment } from "react-relay";

import { useClaudeMarketplace } from "app/hooks/config/useClaudeMarketplace/useClaudeMarketplace";
import { useMCPEnabled } from "app/hooks/feature-flags/useMCPEnabled/useMCPEnabled";

import { ControlPlusEnablement } from "../ControlPlusEnablement";
import { MCP_CLIENT_SECTIONS, McpClientSections } from "./McpClientSections";
import { McpSection } from "./McpSection";

import type { McpSettings_query$key } from "./__generated__/McpSettings_query.graphql";

interface Props {
  queryRef: McpSettings_query$key;
}

/**
 * Jumps to a client's install instructions, taking focus with it: a keyboard
 * user who presses one of these otherwise ends up reading a section their
 * focus is nowhere near. `scrollIntoView` moves the viewport and `focus` the
 * caret, so the section is announced by name once it arrives.
 */
function scrollToSection(id: string) {
  const section = document.getElementById(id);

  if (!section) {
    return;
  }

  // Guarded the way ThemeContext guards it: jsdom has no matchMedia.
  const prefersReducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  section.scrollIntoView({
    behavior: prefersReducedMotion ? "auto" : "smooth",
  });
  section.focus({ preventScroll: true });
}

/**
 * Stacklet MCP settings. Like the Jun0 page it sits beside, enablement is a
 * deployment change rather than a console one — the endpoint is either in
 * `application.json` or it isn't — so the switch only ever reports state.
 *
 * What the page is really for is everything below it: the endpoint is useless
 * until it is in an MCP client's config, so a deployment that has one gets
 * per-client install instructions, and one that doesn't gets the pitch instead.
 */
export function McpSettings({ queryRef }: Props) {
  const data = useFragment(
    graphql`
      fragment McpSettings_query on Query {
        ...useClaudeMarketplace_query
        ...useMCPEnabled_query
      }
    `,
    queryRef,
  );

  // `mcp_url` in the deployment's config is the whole enablement signal, so the
  // switch and the install instructions read from the same place: without an
  // endpoint there is nothing to point a client at, and nothing to switch on.
  const { isEnabled, url } = useMCPEnabled(data);
  const marketplace = useClaudeMarketplace(data);

  return (
    <div className="flex flex-col gap-2xl">
      <Heading className="text-display-small" level={1}>
        Stacklet MCP
      </Heading>

      <div className="flex flex-col gap-md">
        <p className="text-body-medium text-text-secondary">
          Stacklet MCP connects Claude (or any MCP-compatible client) directly
          to your Stacklet environment — query governance data, inspect policies
          and bindings, and pull asset inventory and compliance findings, all
          through natural language.
        </p>

        <div className="text-body-medium text-text-secondary">
          <p>You can authenticate to Stacklet MCP through two options:</p>
          <ul className="list-disc ps-xl">
            <li>OAuth</li>
            <li>Personal API Key</li>
          </ul>
        </div>

        {/* Only the plugin brings the skills, so the pitch waits for it. */}
        {marketplace ? (
          <p className="text-body-medium text-text-secondary">
            You can use Stacklet Agent Skills in Claude Code. Agent Skills are
            guardrails derived from Stacklet platform’s policy posture —
            steering coding agents away from defining infrastructure that
            violates deployed policies.
          </p>
        ) : null}
      </div>

      <ControlPlusEnablement feature="Stacklet MCP" isEnabled={isEnabled} />

      {/*
        No endpoint means no command worth copying, so the install instructions
        appear only once there is something to point a client at.
      */}
      {url ? (
        <div className="flex flex-col gap-xl">
          <McpSection id="mcp-clients" title="Code Editors">
            <div className="flex flex-wrap gap-md">
              {Object.values(MCP_CLIENT_SECTIONS).map(({ id, label }) => (
                // A button rather than an in-page anchor: react-aria links go
                // through the router bridge, which turns a bare "#id" into a
                // route change that scrolls nowhere.
                <Button
                  key={id}
                  onPress={() => scrollToSection(id)}
                  variant="tertiary"
                >
                  {label}
                </Button>
              ))}
            </div>
          </McpSection>

          <McpClientSections marketplace={marketplace} url={url} />
        </div>
      ) : null}
    </div>
  );
}
